import React, { useState, useEffect } from 'react';
import { Search, Plus, SortAsc, SortDesc, Settings, Edit, Trash2, Filter, Eye , AlertTriangle} from 'lucide-react';
import Header from './Header';
import { getUserInfo } from './apiUtils';
import CreateStatusDialog from './ManageStatusComponents/CreateStatusDialog';

// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '0px',
    backgroundColor: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    fontSize: '0.875rem',
  },
  th: {
    backgroundColor: '#E5E4E2',
    color: '#0064e5',
    padding: '6px 12px',
    textAlign: 'left',
    borderBottom: '2px solid #ddd',
    fontSize: '0.875rem',
    lineHeight: '1',
    height: '32px',
    cursor: 'pointer',
    userSelect: 'none',
    position: 'relative'
  },
  thNonSortable: {
    backgroundColor: '#E5E4E2',
    color: '#0064e5',
    padding: '6px 12px',
    textAlign: 'left',
    borderBottom: '2px solid #ddd',
    fontSize: '0.875rem',
    lineHeight: '1',
    height: '32px'
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #ddd',
  },
  checkbox: {
    marginRight: '10px',
  },
  button: {
    padding: '6px 12px',
    margin: '0 5px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  error: {
    color: 'red',
    padding: '10px',
    marginBottom: '10px',
  },
  deleteButton: {
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    padding: '6px 12px',
    height: '28px',
    fontSize: '0.8rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  disabled: {
    opacity: 0.6
  }
};

const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  dialog: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '400px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  title: {
    fontSize: '18px',
    textAlign: 'center',
    margin: 0,
    color: '#000',
    backgroundColor: 'white',
  },
  button: {
    backgroundColor: '#FFFDD0',
    border: 'none',
    borderRadius: '4px',
    padding: '10px',
    width: '100%',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
    color: 'black'
  }
};

const Dialog = ({ message, onClose, onCancel }) => {
  const isConfirmation = message.startsWith('Are you sure');
  const isEmptyStatuses = message.startsWith('No Status Codes found');

  const formattedMessage = message.split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i < message.split('\n').length - 1 && <br />}
    </React.Fragment>
  ));

  return (
    <div style={DialogStyles.overlay}>
      <div style={{
        ...DialogStyles.dialog,
        borderRadius: isEmptyStatuses ? '16px' : '8px',
        border: isEmptyStatuses ? '1px solid #ccc' : 'none',
      }}>
        <h2 style={{
          ...DialogStyles.title,
          fontSize: isEmptyStatuses ? '16.8px' : '18px',
          lineHeight: isEmptyStatuses ? '1.4' : '1.2',
        }}>
          {formattedMessage}
        </h2>
        {isConfirmation ? (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              style={{
                ...DialogStyles.button,
                backgroundColor: '#FF8F89',
                color: 'white',
                width: '50%'
              }}
              onClick={onClose}
            >
              Yes
            </button>
            <button
              style={{
                ...DialogStyles.button,
                width: '50%'
              }}
              onClick={onCancel}
            >
              No
            </button>
          </div>
        ) : (
          <button
            style={{
              ...DialogStyles.button,
              backgroundColor: isEmptyStatuses ? '#FFFDD0' : '#FFFDD0',
              width: isEmptyStatuses ? '120px' : '100%',
              margin: isEmptyStatuses ? '0 auto' : '0',
              display: 'block'
            }}
            onClick={onClose}
          >
            OK
          </button>
        )}
      </div>
    </div>
  );
};

const StatusActionsPanel = ({
  permissions,
  selectedStatuses,
  onEditStatus,
  onDeleteStatus,
  onBulkEdit,
  onExportStatuses,
  onViewHistory
}) => {
  return (
    <div className="w-80 bg-white shadow-lg rounded-lg p-6 h-fit">
      {selectedStatuses.size > 0 && (
        <div className="mt-4 p-3 bg-blue-50 rounded-md">
          <p className="text-sm text-blue-600">
            <strong>{selectedStatuses.size}</strong> status{selectedStatuses.size !== 1 ? 'es' : ''} selected
          </p>
        </div>
      )}
    </div>
  );
};

const ManageStatusCodes = () => {
  const [entityHeaders, setEntityHeaders] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedStatuses, setSelectedStatuses] = useState(new Set());
  const [searchInput, setSearchInput] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [permissions, setPermissions] = useState({
    canUpdate: false,
    canDelete: false,
    canViewStatusList: false
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedStatusForEdit, setSelectedStatusForEdit] = useState(null);

  const login_token = localStorage.getItem('authToken');
  const [corporateAccount, setCorporateAccount] = useState('');
  const [currentProject, setCurrentProject] = useState('');
  const [userId, setUserId] = useState('');
  const [accountDescription, setAccountDescription] = useState('');
  const [userName, setUserName] = useState('');
  const [redirectPath, setRedirectPath] = useState('');


const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
const [itemToDelete, setItemToDelete] = useState(null);


  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const showErrorDialog = (message, path) => {
    setDialogMessage(message);
    setRedirectPath(path);
    setShowDialog(true);
  };



const renderDeleteConfirmDialog = () => {
  if (!isDeleteConfirmOpen || !itemToDelete) return null;

  return (
    <div style={{...DialogStyles.overlay, zIndex: 1100}}>
      <div style={{...DialogStyles.dialog, width: '600px'}}>
        <div style={{
          backgroundColor: '#FFE6E6',
          color: '#CC0000',
          padding: '15px 0',
          textAlign: 'center',
          fontSize: '18px',
          fontWeight: 'bold',
          borderRadius: '8px 8px 0 0',
          margin: '-20px -20px 20px -20px'
        }}>
          <div className="flex items-center justify-center gap-2">
            <AlertTriangle size={20} />
            <span>Confirm Deletion</span>
          </div>
        </div>

        <div className="px-6 py-4">
          <p className="text-gray-700 mb-4">
            {itemToDelete.id === 'multiple'
              ? `Are you sure you want to delete these status codes?`
              : `Are you sure you want to delete this status code?`}
          </p>
          <p className="text-sm text-red-600 mb-4">
            This action cannot be undone.
          </p>

          {/* Show status details */}
          <div className="bg-gray-50 rounded p-3 mb-4">
            <p className="text-xs text-gray-600 mb-2">Status details:</p>
            <div className="text-sm text-gray-700">
              {itemToDelete.id === 'multiple' ? (
                <div><strong>Selected Status Codes:</strong> {itemToDelete.description}</div>
              ) : (
                <>
                  <div><strong>Entity:</strong> {selectedEntity?.replace(/_/g, ' ')}</div>
                  <div><strong>Status Code:</strong> {itemToDelete.status}</div>
                  <div><strong>Created Date:</strong> {formatDate(itemToDelete.created_date)}</div>
                  <div><strong>Updated Date:</strong> {formatDate(itemToDelete.updated_date)}</div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-center gap-4 p-6 pt-0">
          <button
            onClick={() => {
              setIsDeleteConfirmOpen(false);
              setItemToDelete(null);
            }}
            style={{
              backgroundColor: '#f8f9fa',
              padding: '8px 16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteConfirmed}
            style={{
              backgroundColor: '#dc3545',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {itemToDelete.id === 'multiple'
              ? 'Delete Status Codes'
              : 'Delete Status Code'}
          </button>
        </div>
      </div>
    </div>
  );
};


  const checkPermission = async (categoryHeader, categorySubHeaders) => {
    console.log(`Checking permissions for ${categoryHeader} with sub-headers:`, categorySubHeaders);

    try {
      const response = await fetch(appConfig.getUrl('/get_user_access_level_new'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          user_id: userId,
          project_id: currentProject,
          category_header: categoryHeader,
          category_sub_header: ""
        }),
      });
      const data = await response.json();
      console.log(`API response for ${categoryHeader}:`, data);

      const newPermissions = {};

      data.user_actions_list.forEach(item => {
        console.log(`Processing user action: ${item.user_action}, has_access: ${item.has_access}`);

        switch(item.user_action) {
          case 'Create Or Edit Status Codes':
            newPermissions.canUpdate = item.has_access;
            break;
          case 'Delete Status Codes':
            newPermissions.canDelete = item.has_access;
            break;
          case 'View Status Codes':
            newPermissions.canViewStatusList = item.has_access;
            console.log(`User has permission to view status list: ${item.has_access}`);
            break;

        }
      });

      setPermissions(prevPermissions => ({
        ...prevPermissions,
        ...newPermissions
      }));

      return {
        success: data.access_status && data.status === 'Success',
        permissions: newPermissions
      };
    } catch (error) {
      console.error(`Error checking permission for ${categoryHeader}:`, error);
      return {
        success: false,
        permissions: {}
      };
    }
  };

  const fetchEntityHeaders = async () => {
    try {
      const response = await fetch(appConfig.getUrl('/get_status_headers'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setEntityHeaders(data.entity_headers);
        console.log('Entity headers fetched successfully:', data.entity_headers.length, 'headers');
      } else {
        console.error('Failed to fetch entity headers:', data.status_description);
        setEntityHeaders([]);
      }
    } catch (error) {
      console.error('Error fetching entity headers:', error);
      setEntityHeaders([]);
    }
  };

  const fetchStatuses = async (entity) => {
    setStatuses([]);

    console.log('Fetching statuses for entity:', entity);

    if (!permissions.canViewStatusList) {
      console.log('User does not have permission to view status list');
      return;
    }

    if (!entity) {
      return;
    }

    try {
      const response = await fetch(appConfig.getUrl('/get_statuses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          entity: entity
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        let filteredStatuses = data.entity_statuses;

        if (searchFilter) {
          filteredStatuses = filteredStatuses.filter(status =>
            status.status.toLowerCase().includes(searchFilter.toLowerCase())
          );
        }

        if (sortConfig.key) {
          filteredStatuses.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (aValue < bValue) {
              return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
              return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
          });
        }

        setStatuses(filteredStatuses);
      } else {
        console.error('Failed to fetch statuses:', data.status_description);
        setStatuses([]);
      }
    } catch (error) {
      console.error('Error fetching statuses:', error);
      setStatuses([]);
    }
  };

  const handleEntitySelect = (entity) => {
    setSelectedEntity(entity);
    setSelectedStatuses(new Set());
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearch = () => {
    setSearchFilter(searchInput);
  };

  const handleSearchInputChange = (e) => {
    const newValue = e.target.value;
    setSearchInput(newValue);

    if (newValue === '') {
      setSearchFilter('');
    }
  };

  const getSelectedEntityDisplay = () => {
    return selectedEntity ? selectedEntity.replace(/_/g, ' ') : '';
  };

  const refreshStatuses = () => {
    if (permissions.canViewStatusList && selectedEntity) {
      fetchStatuses(selectedEntity);
    }
    setSelectedStatuses(new Set());
  };

const handleDelete = async () => {
  if (!permissions.canDelete) {
    setError('Insufficient permissions to delete items');
    return;
  }

  if (selectedStatuses.size === 0) {
    setError('Please select at least one status to delete');
    return;
  }

  // For single selection, show detailed confirmation
  if (selectedStatuses.size === 1) {
    const selectedStatusCode = Array.from(selectedStatuses)[0];
    const status = statuses.find(s => s.status === selectedStatusCode);

    setItemToDelete({
      id: selectedStatusCode,
      status: status?.status,
      created_date: status?.created_date,
      updated_date: status?.updated_date
    });

    setIsDeleteConfirmOpen(true);
    return;
  }

  // For multiple selections, create a summary object
  const multipleItemsObject = {
    id: 'multiple',
    description: `${selectedStatuses.size} selected status code${selectedStatuses.size > 1 ? 's' : ''}`,
    actualIds: Array.from(selectedStatuses)
  };

  setItemToDelete(multipleItemsObject);
  setIsDeleteConfirmOpen(true);
};

// 5. Add the handleDeleteConfirmed function:
const handleDeleteConfirmed = async () => {
  setIsDeleteConfirmOpen(false);
  setIsDeleting(true);
  setError(null);

  try {
    // Handle both single and multiple deletions
    const statusCodes = itemToDelete.id === 'multiple'
      ? itemToDelete.actualIds
      : [itemToDelete.id];

    // Note: This API endpoint would need to be implemented on the backend
    const response = await fetch(appConfig.getUrl('/delete_status'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        project_id: currentProject,
        entity: selectedEntity,
        status: statusCodes
      }),
    });

    const data = await response.json();

    if (data.status === 'Success') {
      const message = statusCodes.length === 1
        ? 'Status code deleted successfully'
        : 'Status codes deleted successfully';
      setError(data.status_description || message);

      setTimeout(() => {
        setSelectedStatuses(new Set());
        fetchStatuses(selectedEntity);

        setTimeout(() => {
          setError(null);
        }, 2000);
      }, 2000);

    } else {
      throw new Error(data.status_description || 'Failed to delete status codes');
    }
  } catch (err) {
    setError('Failed to delete status codes: ' + err.message);
  } finally {
    setIsDeleting(false);
    setItemToDelete(null);
  }
};



  // Action handlers for the right panel
  const handleEditStatus = () => {
    console.log('Edit status clicked');
    // Implement edit functionality
  };

  const handleBulkEdit = () => {
    console.log('Bulk edit clicked');
    // Implement bulk edit functionality
  };

  const handleExportStatuses = () => {
    console.log('Export statuses clicked');
    // Implement export functionality
  };

  const handleViewHistory = () => {
    console.log('View history clicked');
    // Implement view history functionality
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    if (redirectPath) {
      window.location.href = redirectPath;
    }

  };

  const handleDialogCancel = () => {
    setShowDialog(false);
  };

  // Initial load useEffect - Get user info
  useEffect(() => {
    const setInitialLoad = async () => {
      if (!login_token) {
        showErrorDialog('Invalid login credentials. Please login first', '/LoginAndAuthentication');
        return;
      }

      try {
        const result = await getUserInfo({
          login_token: login_token
        });

        if (result.success) {
          setUserId(result.user_id);
          setUserName(result.userInfo.user_name);
          setCorporateAccount(result.userInfo.corporate_account);
          setAccountDescription(result.userInfo.account_description);
          setCurrentProject(result.userInfo.default_project || 'default');
        } else {
          showErrorDialog('Invalid login credentials. Please login again', '/LoginAndAuthentication');
        }
      } catch (error) {
        console.error('Error calling API:', error);
      }
    };

    setInitialLoad();
  }, []);

  // Main initialization useEffect - Check permissions and fetch entity headers
  useEffect(() => {
    const initializeApp = async () => {
      if (!userId || !corporateAccount) return;

      try {
        console.log('Starting app initialization...');

        setEntityHeaders([]);

        // Check permissions
        console.log('Checking permissions...');
        const result = await checkPermission('Admin Functions', ['Create Or Edit Status Codes', 'Delete Status Codes', 'View Status Codes' ]);

        // Fetch entity headers
        console.log('Fetching entity headers...');
        await fetchEntityHeaders();

        console.log('App initialization complete');

      } catch (error) {
        console.error('Error initializing app:', error);
      }
    };

    initializeApp();
  }, [userId, corporateAccount]);

  // Statuses fetching useEffect
  useEffect(() => {
    console.log('useEffect for selectedEntity, searchFilter, sortConfig, permissions.canViewStatusList triggered --', selectedEntity);
    console.log('can View Status List Permission:', permissions.canViewStatusList);

    if (selectedEntity && permissions.canViewStatusList) {
      console.log('Fetching statuses for selected entity:', selectedEntity);
      fetchStatuses(selectedEntity);
    } else if (selectedEntity && !permissions.canViewStatusList) {
      setStatuses([]);
    }
  }, [selectedEntity, searchFilter, sortConfig, permissions.canViewStatusList]);

  // Auto-select entity if only one exists
  useEffect(() => {
    if (entityHeaders.length === 1 && !selectedEntity) {
      const singleEntity = entityHeaders[0];
      console.log('Auto-selecting single entity:', singleEntity.entity);
      setSelectedEntity(singleEntity.entity);
      setSelectedStatuses(new Set());
      setCurrentPage(1);
    }
  }, [entityHeaders, selectedEntity]);

  // Pagination useEffects
  useEffect(() => {
    setCurrentPage(1);
  }, [searchFilter, sortConfig]);

  useEffect(() => {
    const calculatedTotalPages = Math.ceil(statuses.length / itemsPerPage);
    if (currentPage > Math.max(1, calculatedTotalPages)) {
      setCurrentPage(Math.max(1, calculatedTotalPages));
    }
  }, [statuses, currentPage, itemsPerPage]);

  // Add pagination calculation logic
  const totalPages = Math.ceil(statuses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedStatuses = statuses.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div style={{ marginTop: '80px' }}>
        {/* Header section */}
        <div className="pt-2 pb-2">
          <div className="flex items-center px-6 mb-0 justify-between">
            <div className="w-80">
              <div className="flex items-center relative">
                <input
                  type="text"
                  value={searchInput}
                  onChange={handleSearchInputChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  placeholder="Search status codes..."
                  className="p-2 pr-4 border rounded w-full"
                />
                <button
                  onClick={handleSearch}
                  className="ml-2 p-1 rounded bg-blue-500 hover:bg-blue-600 text-white"
                  aria-label="Search"
                >
                  <Search size={16} />
                </button>
              </div>
            </div>

            <h1 className="text-xl font-bold text-center flex-1">Manage Status Codes</h1>

            <div className="w-80"></div>
          </div>

          <div className="flex items-center px-6 mb-0 bg-blue-100 py-0 rounded-lg">
            {/* Selected Entity Path - Left aligned */}
            <div className="flex-1 text-left">
              <span className="text-base font-bold text-blue-700">Selected Entity: </span>
              <span className="text-sm">
                {selectedEntity ? (
                  <>
                    <span className="text-green-600">
                      {getSelectedEntityDisplay()} ({selectedEntity})
                    </span>
                    <button
                      onClick={() => {
                        setSelectedEntity(null);
                        setStatuses([]);
                      }}
                      className="px-3 py-1 text-xs bg-red-300 text-white rounded hover:bg-red-500 items-center ml-4">
                      X Clear Selection
                    </button>
                  </>
                ) : (
                  <span style={{ color: 'red' }}>None Selected</span>
                )}
              </span>
            </div>

            {/* Current Project Info - Center aligned */}
            <div className="flex-1 text-center">
              <h2 className="text-base font-bold text-blue-700">Project: {currentProject}</h2>
            </div>

            {/* Create Status Button - Right aligned */}
            <div className="flex-1 flex justify-end items-center relative group">
              <button
                disabled={!selectedEntity || !permissions.canUpdate}
                onClick={() => {
                  if (selectedEntity && permissions.canUpdate) {
                    setSelectedStatusForEdit(null);
                    setIsEditMode(false);
                    setIsCreateDialogOpen(true);
                  } else if (!selectedEntity) {
                    setDialogMessage('Please select an entity from the left panel first');
                    setShowDialog(true);
                  } else if (!permissions.canUpdate) {
                    setDialogMessage('You do not have permission to create or edit status codes');
                    setShowDialog(true);
                  }
                }}
                className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${(!selectedEntity || !permissions.canUpdate) ? 'opacity-50' : ''}`}
              >
                <Plus size={18} className="mr-2" />
                <span>Create New Status</span>
              </button>

              {(!permissions.canUpdate || !selectedEntity) && (
                <div className="absolute right-0 top-full mt-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                  {!selectedEntity
                    ? "Select an entity from the left panel to enable this button"
                    : !permissions.canUpdate
                    ? "You do not have permission to create or edit status codes"
                    : ""}
                  <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-600"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-64px)]">
        {/* Left Panel - Entity Headers */}
        <div className="mt-0 w-64 text-white bg-blue-800 border-r overflow-y-auto">
          <div className="p-4">
            <div className="flex justify-center items-center mb-4 bg-white p-2 rounded-lg shadow">
              <h2 className="font-semibold text-sm text-blue-600">Select Category</h2>
            </div>

            {entityHeaders.map(entity => (
              <div
                key={entity.entity}
                className={`flex items-center py-2 cursor-pointer ${
                  selectedEntity === entity.entity ? 'bg-blue-100' : ''
                }`}
                style={{
                  backgroundColor: selectedEntity === entity.entity ? '#DBEAFE' : 'transparent',
                  color: selectedEntity === entity.entity ? '#1E40AF' : 'white',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (selectedEntity !== entity.entity) {
                    e.currentTarget.style.backgroundColor = '#fcfafc';
                    e.currentTarget.style.color = '#007bff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedEntity !== entity.entity) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = 'white';
                  }
                }}
                onClick={() => handleEntitySelect(entity.entity)}
              >
                <span className="ml-2 text-sm">
                  {entity.entity.replace(/_/g, ' ')}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex gap-4">
          {/* Main table area */}
          <div className="flex-1 overflow-y-auto p-0 relative">
            {!permissions.canViewStatusList ? (
              <div className="flex items-center justify-center h-full">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 max-w-md mx-auto">
                  <div className="flex items-center justify-center mb-4">
                    <svg className="w-12 h-12 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-yellow-800 mb-3 text-center">Access Restricted</h3>
                  <p className="text-yellow-700 text-center">
                    You do not have permission to view the status list. Please contact your administrator to request access.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex-1">
                <div className="bg-white rounded-lg shadow">
                  <table style={styles.table}>
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th style={styles.thNonSortable}>
                          <input
                            type="checkbox"
                            checked={statuses.length > 0 && selectedStatuses.size === statuses.length}
                            onChange={(e) => {
                              const newSelected = new Set();
                              if (e.target.checked) {
                                statuses.forEach(status => newSelected.add(status.status));
                              }
                              setSelectedStatuses(newSelected);
                            }}
                          />
                        </th>
                        <th
                          style={styles.th}
                          onClick={() => handleSort('status')}
                          className="select-none hover:bg-gray-200 transition-colors"
                        >
                          <div className="flex items-center">
                            <span style={{ textDecoration: 'underline' }}>Status Code</span>
                            <span className="ml-1">
                              {sortConfig.key === 'status' ? (
                                sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                              ) : null}
                            </span>
                          </div>
                        </th>
                        <th
                          style={styles.th}
                          onClick={() => handleSort('created_date')}
                          className="select-none hover:bg-gray-200 transition-colors"
                        >
                          <div className="flex items-center">
                            <span style={{ textDecoration: 'underline' }}>Created Date</span>
                            <span className="ml-1">
                              {sortConfig.key === 'created_date' ? (
                                sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                              ) : null}
                            </span>
                          </div>
                        </th>
                        <th
                          style={styles.th}
                          onClick={() => handleSort('updated_date')}
                          className="select-none hover:bg-gray-200 transition-colors"
                        >
                          <div className="flex items-center">
                            <span style={{ textDecoration: 'underline' }}>Updated Date</span>
                            <span className="ml-1">
                              {sortConfig.key === 'updated_date' ? (
                                sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                              ) : null}
                            </span>
                          </div>
                        </th>
                      </tr>
                    </thead>




             <tbody>
  {paginatedStatuses.map(status => (
    <tr
      key={status.status}
      className="border-t hover:bg-gray-50"
    >
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={selectedStatuses.has(status.status)}
          onChange={() => {
            const newSelected = new Set(selectedStatuses);
            if (newSelected.has(status.status)) {
              newSelected.delete(status.status);
            } else {
              newSelected.add(status.status);
            }
            setSelectedStatuses(newSelected);
          }}
        />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {status.icon_url && (
            <img
              src={status.icon_url}
              alt={`${status.status} icon`}
              style={{
                width: '48px',
                height: '48px',
                objectFit: 'contain'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          )}
          <span>{status.status}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="text-xs text-gray-600">
          {formatDate(status.created_date)}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="text-xs text-gray-600">
          {formatDate(status.updated_date)}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="relative group">
          <button
            onClick={() => {
              if (permissions.canUpdate) {
                setSelectedStatusForEdit(status);
                setIsEditMode(true);
                setIsCreateDialogOpen(true);
              }
            }}
            disabled={!permissions.canUpdate}
            className={`flex items-center px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs ${
              !permissions.canUpdate ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <Edit size={14} className="mr-1" />
            Edit
          </button>

          {!permissions.canUpdate && (
            <div className="absolute right-0 top-full mt-2 px-3 py-2 bg-gray-600 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
              You do not have permission to edit status codes
              <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-600"></div>
            </div>
          )}
        </div>
      </td>
    </tr>
  ))}



{statuses.length === 0 && (
  <tr>
    <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
      No status codes found. {selectedEntity ? 'Create a new status or adjust your search.' : 'Select an entity to view status codes.'}
    </td>
  </tr>
)}




</tbody>



                  </table>
                </div>

                {/* Error/Success Messages */}
                {error && (
                  <div className={`mb-4 p-2 rounded ${
                    error.toLowerCase().includes('successfully')
                      ? 'bg-green-50 text-green-700'
                      : 'bg-red-50 text-red-500'
                  }`}>
                    {error}
                  </div>
                )}

                {/* Pagination and status count information */}
                {statuses.length > 0 && (
                  <div className="mt-4 flex justify-between items-center bg-white p-4 rounded-lg shadow">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span>Total Status Codes: {statuses.length}</span>
                      {selectedStatuses.size > 0 && (
                        <span>| Selected: {selectedStatuses.size}</span>
                      )}
                      <span>| Showing {statuses.length > 0 ? startIndex + 1 : 0} - {Math.min(startIndex + itemsPerPage, statuses.length)} of {statuses.length}</span>
                    </div>

                    {/* Pagination controls */}
                    <div className="flex items-center gap-2">
                      <button
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        aria-label="Previous page"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <span className="text-sm text-blue-600">Page {currentPage} of {Math.max(1, totalPages)}</span>
                      <button
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages || totalPages === 0}
                        aria-label="Next page"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>

                    {/* Items per page selector */}
                    <div className="flex items-center">
                      <span className="text-sm text-gray-600 mr-2">Items per page:</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="border rounded p-1 text-sm"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Delete button section */}
                <div className="mt-4 flex justify-start">
                  <div className="group relative">
                    <button
                      style={{
                        ...styles.deleteButton,
                        padding: '8px 16px',
                        ...(isDeleting || selectedStatuses.size === 0 || !permissions.canDelete ? styles.disabled : {})
                      }}
                      onClick={handleDelete}
                      disabled={isDeleting || selectedStatuses.size === 0 || !permissions.canDelete}
                    >
                      {isDeleting ? 'Deleting...' : `Delete Selected (${selectedStatuses.size})`}
                    </button>

                    {/* Delete button tooltip */}
                    {(selectedStatuses.size === 0 || !permissions.canDelete) && (
                      <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                        {selectedStatuses.size === 0
                          ? "Select one or more status codes to delete"
                          : "Insufficient permission"
                        }
                        <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right sidebar with status actions */}
          <StatusActionsPanel
            permissions={permissions}
            selectedStatuses={selectedStatuses}
            onEditStatus={handleEditStatus}
            onDeleteStatus={handleDelete}
            onBulkEdit={handleBulkEdit}
            onExportStatuses={handleExportStatuses}
            onViewHistory={handleViewHistory}
          />
        </div>
      </div>

{showDialog && (
  <Dialog
    message={dialogMessage}
    onClose={handleDialogClose}
    onCancel={handleDialogCancel}
  />
)}

{/* Delete Confirmation Dialog */}
{renderDeleteConfirmDialog()}

      <CreateStatusDialog
        isOpen={isCreateDialogOpen}
        onClose={() => {
          setIsCreateDialogOpen(false);
          setIsEditMode(false);
          setSelectedStatusForEdit(null);
        }}
        onSubmit={refreshStatuses}
        selectedEntity={selectedEntity}
        isEditMode={isEditMode}
        statusToEdit={selectedStatusForEdit}
        permissions={permissions}
        loginToken={login_token}
        corporateAccount={corporateAccount}
        currentProject={currentProject}
      />
    </div>
  );
};

export default ManageStatusCodes;